/**
 * GUI 삼각형 상태 중앙 관리 스토어
 *
 * 모든 코너(topLeft, topRight, bottomLeft, bottomRight)의
 * enabled, controlPoint, animationProgress를 관리합니다.
 *
 * OverlayCanvas, GUIClippingManager 등이 이 스토어를 구독하여
 * 자동으로 동기화됩니다.
 */
import { create } from "zustand";
import {
  GUI_CONFIG,
  type GUICornerConfig,
} from "@/three/config/guiClippingConfig";

export type CornerKey = "topLeft" | "topRight" | "bottomLeft" | "bottomRight";

export interface CornerState {
  /** 활성화 여부 */
  enabled: boolean;
  /** 제어점 (0~1, 빗변 위치 결정) */
  controlPoint: number;
  /** 애니메이션 진행도 (0=보임, 1=숨김) */
  animationProgress: number;
  /** 색상 */
  color: string;
  /** 텍스처 강도 */
  textureIntensity: number;
  /** 페이드 설정들 */
  fadeEnabled: boolean;
  fadeStart: number;
  fadeEnd: number;
  fadeMinAlpha: number;
  fadeMaxAlpha: number;
  fadeKnot: number;
}

interface GUITriangleState {
  /** 각 코너별 상태 */
  corners: Record<CornerKey, CornerState>;

  // ============ Actions ============

  /** 코너 활성화 설정 */
  setEnabled: (corner: CornerKey, enabled: boolean) => void;

  /** 제어점 설정 */
  setControlPoint: (corner: CornerKey, value: number) => void;

  /** 애니메이션 진행도 설정 (useGUITriangleAnimation에서 호출) */
  setAnimationProgress: (corner: CornerKey, progress: number) => void;

  /** 코너 전체 설정 업데이트 */
  updateCorner: (corner: CornerKey, config: Partial<CornerState>) => void;

  // ============ Getters ============

  /** 특정 코너의 전체 상태 가져오기 */
  getCornerState: (corner: CornerKey) => CornerState;

  /** GUIClippingManager용 설정 생성 */
  getClippingConfig: (corner: CornerKey) => {
    enabled: boolean;
    controlPoint: number;
    hypotenuseOffset: number;
  };
}

/** GUICornerConfig를 CornerState로 변환 */
function configToState(
  config: GUICornerConfig,
  animationProgress: number = 1
): CornerState {
  return {
    enabled: config.enabled,
    controlPoint: config.controlPoint,
    animationProgress,
    color: config.color,
    textureIntensity: config.textureIntensity ?? 0.4,
    fadeEnabled: config.fadeEnabled ?? true,
    fadeStart: config.fadeStart ?? 0.0,
    fadeEnd: config.fadeEnd ?? 0.85,
    fadeMinAlpha: config.fadeMinAlpha ?? 0.3,
    fadeMaxAlpha: config.fadeMaxAlpha ?? 1.0,
    fadeKnot: config.fadeKnot ?? 0.5,
  };
}

/** 초기 상태 생성 (다크 모드 전용) */
function createInitialState(): Record<CornerKey, CornerState> {
  return {
    topLeft: configToState(GUI_CONFIG.topLeft),
    topRight: configToState(GUI_CONFIG.topRight),
    bottomLeft: configToState(GUI_CONFIG.bottomLeft),
    bottomRight: configToState(GUI_CONFIG.bottomRight),
  };
}

export const useGUITriangleStore = create<GUITriangleState>((set, get) => ({
  corners: createInitialState(),

  setEnabled: (corner, enabled) =>
    set((state) => ({
      corners: {
        ...state.corners,
        [corner]: {
          ...state.corners[corner],
          enabled,
        },
      },
    })),

  setControlPoint: (corner, value) =>
    set((state) => ({
      corners: {
        ...state.corners,
        [corner]: {
          ...state.corners[corner],
          controlPoint: value,
        },
      },
    })),

  setAnimationProgress: (corner, progress) =>
    set((state) => ({
      corners: {
        ...state.corners,
        [corner]: {
          ...state.corners[corner],
          animationProgress: progress,
        },
      },
    })),

  updateCorner: (corner, config) =>
    set((state) => ({
      corners: {
        ...state.corners,
        [corner]: {
          ...state.corners[corner],
          ...config,
        },
      },
    })),

  getCornerState: (corner) => get().corners[corner],

  getClippingConfig: (corner) => {
    const cornerState = get().corners[corner];
    return {
      enabled: cornerState.enabled,
      controlPoint: cornerState.controlPoint,
      hypotenuseOffset: cornerState.animationProgress,
    };
  },
}));
